import { isPlatformBrowser, ViewportScroller } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { StorageService } from '@gb-webapp/gb-app';
import { TranslatePipe } from '@ngx-translate/core';
import { filter, Subscription } from 'rxjs';
import { NavigationStateService } from '../../core/services/navigation-state/navigation-state.service';
import { Country, Region } from '../../interfaces/cached-content';
import { GbTheme, THEME_SIGNAL } from '../../interfaces/theme';
import { GoogleTagService } from '../../services/google-tag/google-tag.service';
import { CacheService } from '../../ssr-services/cache/cache.service';
import { AnimatedSearchComponent } from '../animated-search/animated-search.component';
import { ArrowComponent } from '../arrow/arrow.component';
import { CountryComponent } from '../country/country.component';
import { RegionComponent } from '../region/region.component';
import { ToggleComponent } from '../toggle/toggle.component';

@Component({
  selector: 'app-destination-selector',
  imports: [
    ToggleComponent,
    CountryComponent,
    RegionComponent,
    AnimatedSearchComponent,
    TranslatePipe,
    ArrowComponent,
  ],
  templateUrl: './destination-selector.component.html',
  styleUrl: './destination-selector.component.scss',
})
export class DestinationSelectorComponent implements OnInit, OnDestroy {
  isSearchActiveForMobile = signal(true);

  readonly theme = inject(THEME_SIGNAL);

  public GbTheme = GbTheme;

  selectedDestination = signal<'Countries' | 'Regions'>('Countries');
  public isDropdownOpen = signal(false);

  private readonly router = inject(Router);

  public cacheService = inject(CacheService);

  protected gtmService = inject(GoogleTagService);

  protected navigationStateService = inject(NavigationStateService);

  private readonly platformId = inject(PLATFORM_ID);

  protected readonly storageService = inject(StorageService);

  private navEndSub?: Subscription;

  private readonly viewportScroller = inject(ViewportScroller);

  constructor() {
    // Sync destination selection changes with service (called in injection context)
    effect(() => {
      const currentDestination = this.selectedDestination();
      this.navigationStateService.setSelectedToggle(currentDestination);
    });

    // On home: when toggle changes, navigate so URL matches (/, /countries, /regions)
    effect(() => {
      const selected = this.selectedDestination();
      if (!this.isOnHomeDestinationRoutes(this.router.url)) return;
      if (selected !== this.getDestinationFromUrl()) {
        const scrollPosition = this.viewportScroller.getScrollPosition();

        this.router.navigate(selected === 'Regions' ? ['/regions'] : ['/countries']).then(() => {
          // Restore scroll position after navigation completes
          setTimeout(() => {
            this.viewportScroller.scrollToPosition(scrollPosition);
          }, 0);
        });
      }
    });
  }

  isOnHomeDestinationRoutes(url: string): boolean {
    return url === '/' || url === '' || url.includes('/countries') || url.includes('/regions');
  }

  getDestinationFromUrl(): 'Countries' | 'Regions' {
    return this.router.url.includes('/regions') ? 'Regions' : 'Countries';
  }

  syncSelectionFromUrlWhenOnHome(): void {
    if (this.isOnHomeDestinationRoutes(this.router.url)) {
      this.selectedDestination.set(this.getDestinationFromUrl());
    }
  }

  readonly regions = computed(() =>
    [...this.cacheService.allRegions()].sort((a, b) => a.name.localeCompare(b.name))
  );

  /** All countries with plans: popular first (by preference), then rest alphabetically. Non-popular get hidden class for SEO. */
  readonly countries = computed(() => {
    const all = this.cacheService.allCountries().filter((c) => c.plans?.length > 0);
    const popular = all
      .filter((c) => c.isPopular)
      .sort((a, b) => (a.preference || 0) - (b.preference || 0));
    const rest = all.filter((c) => !c.isPopular).sort((a, b) => a.name.localeCompare(b.name));
    return [...popular, ...rest];
  });

  readonly allCountries = computed(() =>
    [...this.cacheService.allCountries()].sort((a, b) => a.name.localeCompare(b.name))
  );

  readonly globalRegions = computed(() => this.cacheService.globalRegions());

  readonly hasGlobalRegionSlug = computed(() => {
    const regions = this.globalRegions();
    return Array.isArray(regions) && regions.length > 0 && !!regions[0]?.slug;
  });

  ngOnInit(): void {
    this.syncSelectionFromUrlWhenOnHome();
    if (!this.isOnHomeDestinationRoutes(this.router.url)) {
      const saved = this.navigationStateService.getSelectedToggle();
      if (saved) this.selectedDestination.set(saved);
    }

    this.navEndSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.syncSelectionFromUrlWhenOnHome());

    this.gtmService.pushEvent('country_list', {
      items: JSON.stringify(this.allCountries().map((c) => ({ id: c._id, name: c.name }))),
      pageUrl: this.router.url,
    });

    this.gtmService.pushEvent('region_list', {
      items: JSON.stringify(this.regions().map((r) => ({ id: r._id, name: r.name }))),
      pageUrl: this.router.url,
    });

    this.gtmService.pushEvent('global_plan_list', {
      items: JSON.stringify(this.globalRegions().map((g) => ({ id: g._id, name: g.name }))),
      pageUrl: this.router.url,
    });
  }

  toggleMobileSearch(): void {
    this.isSearchActiveForMobile.set(!this.isSearchActiveForMobile());
  }

  viewAll(): void {
    this.router.navigate(['/destination'], {
      state: {
        showAllCountries: true,
        selectedToggle: this.selectedDestination(),
        hideViewAllButton: true,
      },
    });
  }

  onCountrySelected(country: Country): void {
    this.gtmService.pushEvent('country_select', {
      countryId: country._id,
      countryName: country.name,
      pageUrl: this.router.url,
    });

    this.clearCheckoutDataIfNotNewPlan();
    this.router.navigate([`/${country.slug}`]);
  }

  onRegionSelected(region: Region): void {
    this.clearCheckoutDataIfNotNewPlan();
    this.router.navigate([`/${region.slug}`]);
  }

  exploreGlobalPlans(): void {
    this.clearCheckoutDataIfNotNewPlan();
    const globalRegion = this.globalRegions()[0];
    if (globalRegion?.slug != null) {
      this.router.navigate([`/${globalRegion.slug}`]);
    }
  }

  showDropdown(): void {
    this.isDropdownOpen.set(true);
  }

  ngOnDestroy(): void {
    this.navEndSub?.unsubscribe();
  }

  clearCheckoutDataIfNotNewPlan(): void {
    if (isPlatformBrowser(this.platformId)) {
      const esimData = this.storageService.getEsimData();

      if (esimData && !esimData?.isNewPlan && !esimData?.isNewSim) {
        this.storageService.clearCheckoutData();
      }
    }
  }
}
